import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import type {ImageInput} from "@khan/guide-types";

import {ImageAttachModalWrapper} from "../image-attach/image-attach-modal-wrapper.tsx";
import {useUserMayInputImages} from "../image-attach/use-user-may-input-images.ts";

import paperclip from "@phosphor-icons/core/regular/paperclip.svg";

interface AttachButtonProps {
    disabled?: true;
    openModal?: () => void;
}

const AttachButton = ({disabled, openModal}: AttachButtonProps) => (
    <IconButton
        disabled={disabled}
        style={styles.imageAttachIcon}
        icon={paperclip}
        aria-label={t`Attach image`}
        size="small"
        kind="tertiary"
        actionType="neutral"
        onClick={openModal}
        testId="image-attach-button"
    />
);

interface LauncherProps {
    onAttachImages: (images: ImageInput[]) => void;
}

/**
 * A button that launches the image input modal.
 */
export function ImageInputModalLauncher({onAttachImages}: LauncherProps) {
    const userMayInputImages = useUserMayInputImages();

    // We don't render the button if it's disallowed for a user.  During playtesting,
    // users were confused about why the button was there if it didn't do anything,
    // even though the tooltip explained that the district hadn't enabled it.
    // So we don't render the button at all.
    if (!userMayInputImages) {
        return null;
    }

    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <ImageAttachModalWrapper
                    onCloseModal={closeModal}
                    onAttachImages={onAttachImages}
                />
            )}
            backdropDismissEnabled={true}
        >
            {({openModal}) => (
                <Tooltip content={t`Attach image`}>
                    <AttachButton openModal={openModal} />
                </Tooltip>
            )}
        </ModalLauncher>
    );
}

const styles = StyleSheet.create({
    imageAttachIcon: {
        alignSelf: "center",
    },
});
